// How a ticket relates to other tickets: its parent, its children, and its links.
//
// Two different relationships, kept visibly apart because they mean different things
// (docs/API.md §3.2, §3.7):
//
//   * Parent / children is a HIERARCHY, one level deep, for sub-issues raised on the
//     same call. The (+) here is how a child is made: it opens the create screen with
//     everything editable copied from this ticket (`?parent=`), so the agent changes
//     the one thing that differs. A child cannot have children, so the (+) is not
//     offered on one.
//   * Related tickets are a symmetric LINK with no behaviour — "about the same thing".
//     Any ticket, to any ticket, added and removed here; the other ticket shows the
//     same link from its side.
//
// Everything arrives in the one detail response, so nothing here fetches to render;
// only linking and unlinking call the API, and both hand the parent a refresh rather
// than patching local state, because a link lands events on two tickets and the
// activity log below is one of the things that has to move.

import { useState } from 'react';
import { Link } from 'react-router';

import { api } from '../../api/client.js';
import { StatusBadge } from './Badges.jsx';
import TicketLinkPicker from './TicketLinkPicker.jsx';

function Section({ title, action, children }) {
  return (
    <section className="border-t border-line px-4 py-3 first:border-t-0">
      <h3 className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-ink-secondary">
        {title}
        {action}
      </h3>
      <div className="mt-2 space-y-1.5 text-sm">{children}</div>
    </section>
  );
}

function TicketRow({ ticket, trailing }) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-md border border-line px-2 py-1.5">
      <Link to={`/tickets/${ticket.public_ref}`} className="min-w-0 grow hover:underline">
        <span className="font-mono text-xs text-ink-muted">#{ticket.public_ref}</span>
        <span className="block truncate text-sm text-ink">{ticket.subject}</span>
      </Link>
      <span className="flex shrink-0 items-center gap-1.5">
        <StatusBadge status={ticket.status} />
        {trailing}
      </span>
    </div>
  );
}

export default function RelatedTickets({ ticket, canWrite, onChanged }) {
  const [busy, setBusy] = useState(null); // the public_ref being linked/unlinked
  const [error, setError] = useState(null);

  const isChild = Boolean(ticket.parent);
  const linkedIds = ticket.links.map((link) => link.id);

  async function link(candidate) {
    setBusy(candidate.public_ref);
    setError(null);
    try {
      await api.post(`/tickets/${encodeURIComponent(ticket.public_ref)}/links`, {
        ticket: `#${candidate.public_ref}`,
      });
      await onChanged?.();
    } catch (err) {
      setError(err?.message ?? 'Could not link that ticket.');
    } finally {
      setBusy(null);
    }
  }

  async function unlink(other) {
    setBusy(other.public_ref);
    setError(null);
    try {
      await api.del(
        `/tickets/${encodeURIComponent(ticket.public_ref)}/links/${encodeURIComponent(other.public_ref)}`,
      );
      await onChanged?.();
    } catch (err) {
      setError(err?.message ?? 'Could not remove that link.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <aside className="divide-y divide-line rounded-lg border border-line bg-surface">
      {isChild && (
        <Section title="Parent ticket">
          <TicketRow ticket={ticket.parent} />
          <p className="text-xs text-ink-muted">
            A sub-issue from the same call. Worked and closed independently of its parent.
          </p>
        </Section>
      )}

      {/* Nesting is one level deep (migration 004), so a child gets no (+) and never
          lists children of its own. */}
      {!isChild && (
        <Section
          title={`Child tickets${ticket.children.length ? ` (${ticket.children.length})` : ''}`}
          action={
            canWrite && (
              <Link
                to={`/tickets/new?parent=${encodeURIComponent(ticket.public_ref)}`}
                className="btn-ghost -my-1 px-2 py-0.5 text-base leading-none normal-case tracking-normal"
                title="Open a child ticket for another issue from this call"
                aria-label="New child ticket"
              >
                +
              </Link>
            )
          }
        >
          {ticket.children.length > 0 ? (
            ticket.children.map((child) => <TicketRow key={child.id} ticket={child} />)
          ) : (
            <p className="text-xs text-ink-muted">
              None. Press + to split another issue from this call into its own ticket, pre-filled from this one.
            </p>
          )}
        </Section>
      )}

      <Section title={`Related tickets${ticket.links.length ? ` (${ticket.links.length})` : ''}`}>
        {ticket.links.map((other) => (
          <TicketRow
            key={other.id}
            ticket={other}
            trailing={
              canWrite && (
                <button
                  type="button"
                  className="text-xs text-ink-secondary hover:underline disabled:opacity-50"
                  disabled={busy !== null}
                  onClick={() => unlink(other)}
                  title={other.linked_by ? `Linked by ${other.linked_by.display_name}` : undefined}
                >
                  {busy === other.public_ref ? '…' : 'Unlink'}
                </button>
              )
            }
          />
        ))}
        {canWrite ? (
          <div className="pt-1">
            <label className="sr-only" htmlFor={`link-picker-${ticket.id}`}>Link a ticket</label>
            <TicketLinkPicker
              inputId={`link-picker-${ticket.id}`}
              excludeIds={[ticket.id, ...linkedIds]}
              onPick={link}
              disabled={busy !== null}
              placeholder="Link a ticket: number or subject…"
            />
            {error && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>}
            <p className="mt-1 text-xs text-ink-muted">
              Related tickets are about the same thing. Linking changes nothing on either ticket.
            </p>
          </div>
        ) : (
          ticket.links.length === 0 && <p className="text-xs text-ink-muted">None.</p>
        )}
      </Section>
    </aside>
  );
}
